import { useRef, useState } from "react";
import { Control, Controller, FieldError as RHFFieldError } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { BsFileEarmarkText, BsUpload } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { uploadOneDocument } from "../../../../../services/document.service";

export const FieldError = ({ error }: { error?: RHFFieldError | { message?: string } }) =>
	error?.message ? <p className="text-red-500 text-xs mt-1">{error.message}</p> : null;

export const SectionHeading = ({
	title,
	description,
}: {
	title: string;
	description?: string;
}) => (
	<div className="mb-5">
		<h3 className="font-semibold text-base text-charcoal">{title}</h3>
		{description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
	</div>
);

interface PhoneFieldProps {
	name: string;
	control: Control<any>;
	required?: string | false;
	/** Validate as 233 followed by nine digits, which the provider requires for office numbers */
	ghanaOnly?: boolean;
	maxLength?: number;
	error?: RHFFieldError;
}

export const PhoneField = ({
	name,
	control,
	required = false,
	ghanaOnly,
	maxLength = 15,
	error,
}: PhoneFieldProps) => (
	<>
		<Controller
			name={name}
			control={control}
			rules={{
				required,
				validate: (value?: string) => {
					if (!value) return true;
					if (ghanaOnly && !/^233\d{9}$/.test(value)) {
						return "Enter a Ghana number, e.g. 233244000001";
					}
					if (value.length > maxLength) return "Enter a valid phone number";
					return true;
				},
			}}
			render={({ field }) => (
				<div
					className={`w-full bg-secondary-gray rounded-lg mt-2 flex items-center ${
						error ? "border border-red-500" : ""
					}`}
				>
					<PhoneInput
						value={field.value || ""}
						onChange={(value) => field.onChange(value)}
						onBlur={field.onBlur}
						country="gh"
						onlyCountries={ghanaOnly ? ["gh"] : undefined}
						inputStyle={{
							height: "54px",
							border: "none",
							backgroundColor: "#F2F2F4",
							color: "black",
							boxShadow: "none",
							width: "95%",
							margin: "0px 0px 0px 15px",
							borderRadius: "10px",
						}}
						containerStyle={{
							height: "54px",
							border: "none",
							backgroundColor: "#F2F2F4",
							boxShadow: "none",
							borderRadius: "10px",
						}}
						buttonStyle={{
							border: "none",
							backgroundColor: "#F2F2F4",
							boxShadow: "none",
							padding: "0px 10px",
						}}
					/>
				</div>
			)}
		/>
		<FieldError error={error} />
	</>
);

interface FileUploadFieldProps {
	value?: string;
	onChange: (url: string) => void;
	accept?: string;
	disabled?: boolean;
	hasError?: boolean;
	buttonLabel?: string;
}

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // (10 megabytes)

/** Uploads a file to the document service and reports back its URL. */
export const FileUploadField = ({
	value,
	onChange,
	accept = ".pdf,.png,.jpg,.jpeg",
	disabled,
	hasError,
	buttonLabel = "Upload file",
}: FileUploadFieldProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);

	const handleFile = async (file?: File) => {
		if (!file) return;
		if (file.size > MAX_UPLOAD_SIZE) {
			toast.error("File is too large. The maximum size is 10 MB");
			return;
		}

		setUploading(true);
		const formData = new FormData();
		formData.append("file", file);
		const uploadedFile = await uploadOneDocument(formData);
		setUploading(false);

		const url = uploadedFile?.data?.url;
		if (!url) {
			toast.error("File upload failed. Please try again");
			return;
		}
		onChange(url);
	};

	return (
		<div className="mt-2">
			<input
				ref={inputRef}
				type="file"
				accept={accept}
				className="hidden"
				onChange={(event) => {
					handleFile(event.target.files?.[0]);
					event.target.value = "";
				}}
			/>
			{value ? (
				<div className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 h-14">
					<BsFileEarmarkText className="text-xl text-gray-500 shrink-0" />
					<a
						href={value}
						target="_blank"
						rel="noreferrer"
						className="text-sm text-primary-blue truncate flex-1"
					>
						{decodeURIComponent(value.split("/").pop() || "View file")}
					</a>
					{!disabled && (
						<>
							<button
								type="button"
								onClick={() => inputRef.current?.click()}
								className="text-xs text-gray-600 hover:text-primary-blue"
							>
								{uploading ? "Uploading..." : "Replace"}
							</button>
							<button type="button" onClick={() => onChange("")} title="Remove">
								<IoMdClose className="text-lg text-gray-500" />
							</button>
						</>
					)}
				</div>
			) : (
				<button
					type="button"
					disabled={disabled || uploading}
					onClick={() => inputRef.current?.click()}
					className={`w-full h-14 rounded-lg border border-dashed flex items-center justify-center gap-2 text-sm ${
						hasError
							? "border-red-500 text-red-500"
							: "border-gray-300 text-gray-600 hover:border-primary-blue hover:text-primary-blue"
					} disabled:opacity-50`}
				>
					<BsUpload />
					{uploading ? "Uploading..." : buttonLabel}
				</button>
			)}
		</div>
	);
};
